import { OrgSessionEvents } from "@platform/constants/Events";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { StorageKeys } from "@platform/constants/Storage";
import { eventBus } from "@platform/events/EventBus";
import API from "@platform/http/shared-http";
import debugLogger from "@platform/utils/debugLogger";
import { getOrgAliasFromToken, decodeJwt } from "@platform/utils/jwt";

export interface SelectOrgResult {
  token: string;
  orgId: number;
  orgAlias: string;
}

/**
 * OrgSessionManager — single source of truth for the current org-scoped session.
 *
 * Holds the current step-2 JWT token in memory so all consumers read a
 * consistent value, even during an org switch. The token is the authoritative
 * org context — orgAlias and orgId are derived from it.
 *
 * Lifecycle:
 *   1. `initialize()` — read stored token from localStorage on app startup
 *   2. `selectOrg()` — atomic org switch: API call → update memory + localStorage → emit event
 *   3. `clearSession()` — logout path: clear memory + localStorage
 */
class OrgSessionManager {
  private currentToken: string | null = null;
  private switching = false;
  private switchPromise: Promise<SelectOrgResult> | null = null;

  /**
   * Initialize from localStorage. Called once during app startup.
   */
  initialize(): void {
    const stored = localStorage.getItem(StorageKeys.AUTH_TOKEN);
    if (stored) {
      const decoded = decodeJwt(stored);
      if (decoded?.step === 2) {
        this.currentToken = stored;
        debugLogger.info(
          "org-session",
          "Initialized with org-scoped token",
          decoded.orgAlias,
        );
      } else {
        debugLogger.info(
          "org-session",
          "Stored token is step-1 (no org context)",
        );
        this.currentToken = stored;
      }
    }
  }

  /**
   * Get the current auth token. Always reads from in-memory state,
   * never from localStorage directly — so it is never stale during a switch.
   */
  getToken(): string | null {
    return this.currentToken;
  }

  /**
   * Get the current org alias, derived from the JWT.
   * Returns null if no step-2 token is loaded.
   */
  getOrgAlias(): string | null {
    return getOrgAliasFromToken(this.currentToken);
  }

  /**
   * Get the current org ID, derived from the JWT.
   * Returns null if no step-2 token is loaded.
   */
  getOrgId(): number | null {
    if (!this.currentToken) return null;
    const decoded = decodeJwt(this.currentToken);
    return decoded?.orgId ?? null;
  }

  /**
   * Whether an org switch is in progress.
   * API clients should wait for the switch to complete before making requests.
   */
  isSwitching(): boolean {
    return this.switching;
  }

  /**
   * Wait for any in-progress org switch to complete.
   * Returns immediately if no switch is in progress.
   */
  async waitForSwitch(): Promise<void> {
    if (this.switchPromise) {
      await this.switchPromise;
    }
  }

  /**
   * Atomically switch to a different organization.
   *
   * 1. Set switching flag (blocks new API requests via interceptor)
   * 2. Call POST /auth/select-org with the current step-1 token
   * 3. On success: update in-memory token + localStorage, emit ORG_SESSION_CHANGED
   * 4. On failure: emit ORG_SWITCH_FAILED, leave previous session intact
   */
  async selectOrg(orgAlias: string): Promise<SelectOrgResult> {
    if (this.switching) {
      throw new Error("Org switch already in progress");
    }

    this.switching = true;
    this.switchPromise = this.doSelectOrg(orgAlias);

    try {
      const result = await this.switchPromise;
      return result;
    } finally {
      this.switching = false;
      this.switchPromise = null;
    }
  }

  private async doSelectOrg(orgAlias: string): Promise<SelectOrgResult> {
    try {
      const baseUrl = apiBaseUrls.BACKEND;
      const api = new API(baseUrl, "/api/v1/auth/select-org");
      const response = await api.post<SelectOrgResult>({ orgAlias });

      // Atomically update in-memory state + localStorage
      this.currentToken = response.token;
      localStorage.setItem(StorageKeys.AUTH_TOKEN, response.token);

      // Persist org display metadata for sidebar/header
      localStorage.setItem(StorageKeys.CURRENT_ORG_ALIAS, response.orgAlias);

      debugLogger.info("org-session", "Org switched", response.orgAlias);

      eventBus.emitGlobal(OrgSessionEvents.ORG_SESSION_CHANGED, {
        orgAlias: response.orgAlias,
        orgId: response.orgId,
        token: response.token,
      });

      return response;
    } catch (error) {
      debugLogger.error("org-session", "Org switch failed", error);

      eventBus.emitGlobal(OrgSessionEvents.ORG_SWITCH_FAILED, {
        orgAlias,
        error: error instanceof Error ? error.message : "Unknown error",
      });

      throw error;
    }
  }

  /**
   * Clear the entire session. Called on logout.
   * Clears in-memory state and localStorage.
   */
  clearSession(): void {
    this.currentToken = null;
    this.switching = false;
    this.switchPromise = null;
    // Note: full storage cleanup is handled by clearAllRushaStorage() in authService.logout()
  }

  /**
   * Update the in-memory token when login stores a new one.
   * Called after authService.login() or authService.processOAuthToken()
   * to keep the in-memory state in sync with localStorage.
   */
  onTokenStored(token: string): void {
    this.currentToken = token;
  }
}

export const orgSessionManager = new OrgSessionManager();
